import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private refreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(private authService: AuthService) {}

  /**
   * The bearer token goes to OUR API and nowhere else.
   *
   * This attached Authorization to every HttpClient request regardless of
   * destination, so any future call to a third-party URL would have handed that
   * service a working admin credential. Both relative and absolute URLs must resolve to the configured API origin
   * and path prefix; protocol-relative URLs are not implicitly trusted.
   */
  private isOurApi(url: string): boolean {
    try {
      const target = new URL(url, window.location.origin);
      const api = new URL(environment.baseUrl, window.location.origin);
      const prefix = api.pathname.replace(/\/$/, '');
      return target.origin === api.origin &&
        (target.pathname === prefix || target.pathname.indexOf(prefix + '/') === 0);
    } catch {
      return false;
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authService.getAccessToken();
    const authed = token && this.isOurApi(req.url)
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

    return next.handle(authed).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 401 && this.isOurApi(req.url) && !/auth\/(login|refresh)/i.test(req.url)) {
          return this.handle401(req, next);
        }
        return throwError(err);
      }),
    );
  }

  private handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.refreshing) {
      return this.refreshSubject.pipe(
        filter(t => t !== null),
        take(1),
        switchMap(t => next.handle(req.clone({ setHeaders: { Authorization: `Bearer ${t}` } }))),
      );
    }
    this.refreshing = true;
    this.refreshSubject = new BehaviorSubject<string | null>(null);

    return this.authService.refresh().pipe(
      switchMap(newToken => {
        this.refreshing = false;
        this.refreshSubject.next(newToken);
        return next.handle(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }));
      }),
      catchError(err => {
        this.refreshing = false;
        this.refreshSubject.error(err);
        this.authService.logout();
        return throwError(err);
      }),
    );
  }
}
